import { useCallback } from 'react';
import { useUserPermission } from '../useUserPermission';

export enum PackageType {
  Management = 'package:management',
  Eos = 'package:eos',
  Portfolio = 'package:portfolio',
}

export enum PackageTypeWithTier {
  ManagementBasic = 'package:management:basic',
  ManagementStandard = 'package:management:standard',
  ManagementPremium = 'package:management:premium',
  EosBasic = 'package:eos:basic',
  EosStandard = 'package:eos:standard',
  EosPremium = 'package:eos:premium',
  PortfolioBasic = 'package:portfolio:basic',
  PortfolioStandard = 'package:portfolio:standard',
  PortfolioPremium = 'package:portfolio:premium',
}

export const packageRulesConfig = {
  management: [
    PackageTypeWithTier.ManagementBasic,
    PackageTypeWithTier.ManagementStandard,
    PackageTypeWithTier.ManagementPremium,
  ],
  economy: [PackageTypeWithTier.ManagementStandard, PackageTypeWithTier.ManagementPremium],
  eos: [PackageTypeWithTier.EosBasic, PackageTypeWithTier.EosStandard, PackageTypeWithTier.EosPremium],
  portfolio: [PackageTypeWithTier.PortfolioBasic, PackageTypeWithTier.PortfolioStandard, PackageTypeWithTier.PortfolioPremium],
};

const tierRank: Record<string, number> = { basic: 0, standard: 1, premium: 2 };

/**
 * Given the permissions that grant access to a resource, returns the cheapest package + tier the
 * user would need to obtain access, i.e. the lowest tier present in the list. Returns `null` when
 * the list is empty or malformed. Used to tell the user exactly what to purchase/upgrade to.
 */
export function getRequiredPackageAccess(
  permissions: PackageTypeWithTier[],
): { packageName: string; tier: string } | null {
  let best: { packageName: string; tier: string; rank: number } | null = null;
  for (const permission of permissions ?? []) {
    const [, packageName, tier] = permission.split(':');
    if (!packageName || !tier) continue;
    const rank = tierRank[tier] ?? Number.MAX_SAFE_INTEGER;
    if (!best || rank < best.rank) {
      best = { packageName, tier, rank };
    }
  }
  return best ? { packageName: best.packageName, tier: best.tier } : null;
}

export function useUserPackages() {
  const { data, error, isLoading } = useUserPermission();

  const getPackage = useCallback(
    (type: PackageType): boolean => {
      return data?.permissions?.some((p) => p.startsWith(type)) ?? false;
    },
    [data?.permissions],
  );

  const getPackageWithTier = useCallback(
    (type: PackageTypeWithTier): boolean => {
      return data?.permissions?.includes(type) ?? false;
    },
    [data?.permissions],
  );

  const getPackagesWithTier = useCallback(
    (types: PackageTypeWithTier[]): boolean => {
      const permissions = data?.permissions;
      if (!permissions || permissions.length === 0) return false;
      const packagePermission = permissions?.some((p) => types.includes(p as PackageTypeWithTier));
      if (packagePermission) return true;
      return false;
    },
    [data?.permissions],
  );

  const getAvailablePackages = useCallback(() => {
    const management = getPackage(PackageType.Management);
    const economy = getPackagesWithTier(packageRulesConfig.economy);
    const eos = getPackage(PackageType.Eos);
    const portfolio = getPackage(PackageType.Portfolio);
    return {
      management,
      economy,
      eos,
      portfolio,
    };
  }, [getPackage, getPackagesWithTier]);

  return {
    error,
    isLoading,
    getPackage,
    getPackageWithTier,
    getPackagesWithTier,
    getAvailablePackages,
  };
}
